import { useState, useEffect, useCallback } from 'react';
import {
  fetchAdminEntSports, updateAdminSport,
  fetchAdminEntLeagues, updateAdminLeague,
  fetchAdminEntTeams, updateAdminTeam,
  fetchAdminEntMarkets, updateAdminMarket,
} from '@/api';
import { LoadingPage } from '@/components/common/Loading';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Layers, Search, Check, X, Edit2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'sports', label: 'Sports' },
  { key: 'leagues', label: 'Leagues' },
  { key: 'teams', label: 'Teams' },
  { key: 'markets', label: 'Markets' },
];

export default function SportsManagement() {
  const [tab, setTab] = useState('sports');
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editPriority, setEditPriority] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (searchTerm) params.search = searchTerm;

    const fetcher = {
      sports: () => fetchAdminEntSports(params),
      leagues: () => fetchAdminEntLeagues(params),
      teams: () => fetchAdminEntTeams({ ...params, page, limit: 50 }),
      markets: () => fetchAdminEntMarkets(params),
    }[tab];

    fetcher()
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [tab, searchTerm, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSearchTerm(''); setEditingId(null); }, [tab]);

  const handleSave = async (entityId) => {
    const updates = {};
    if (editName) updates.name = editName;
    updates.active = editActive;
    if (editPriority !== undefined) updates.priority = editPriority;

    const updater = { sports: updateAdminSport, leagues: updateAdminLeague, teams: updateAdminTeam, markets: updateAdminMarket }[tab];
    try {
      await updater(entityId, updates);
      toast.success('Updated successfully');
      setEditingId(null);
      load();
    } catch { toast.error('Update failed'); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditName(item.name || '');
    setEditActive(item.active !== false);
    setEditPriority(item.priority || 0);
  };

  const items = data ? (data[tab] || data.teams || []) : [];
  const totalPages = data?.pages || 0;

  return (
    <div data-testid="sports-management-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
          <Layers className="w-5 h-5 inline mr-2 text-cyan-400" />
          Entity Management
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4" data-testid="entity-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
            data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder={`Search ${tab}...`}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-9 bg-slate-900 border-slate-700 text-sm h-9"
          data-testid="entity-search"
        />
      </div>

      {loading ? <LoadingPage /> : (
        <>
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/80">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">ID</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-400 uppercase">Name</th>
                  {tab === 'leagues' && <th className="text-left px-3 py-3 text-xs font-semibold text-slate-400 uppercase">Country</th>}
                  {tab === 'teams' && <th className="text-left px-3 py-3 text-xs font-semibold text-slate-400 uppercase">League</th>}
                  <th className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase">Active</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase">Priority</th>
                  {(tab === 'sports' || tab === 'leagues') && (
                    <th className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase">Stats</th>
                  )}
                  <th className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase w-[100px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`entity-row-${item.id}`}>
                    <td className="px-4 py-3 text-xs text-slate-500 font-mono">{item.id}</td>
                    <td className="px-3 py-3">
                      {editingId === item.id ? (
                        <Input value={editName} onChange={e => setEditName(e.target.value)}
                          className="bg-slate-800 border-slate-700 text-xs h-7 w-full" />
                      ) : (
                        <span className="text-slate-200 font-medium">{item.name}</span>
                      )}
                    </td>
                    {tab === 'leagues' && <td className="px-3 py-3 text-xs text-slate-400">{item.country || '-'}</td>}
                    {tab === 'teams' && <td className="px-3 py-3 text-xs text-slate-400">{item.league_id || '-'}</td>}
                    <td className="px-3 py-3 text-center">
                      {editingId === item.id ? (
                        <button onClick={() => setEditActive(!editActive)}>
                          <Badge className={editActive ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs cursor-pointer' : 'bg-red-500/10 text-red-400 border-red-500/20 text-xs cursor-pointer'}>
                            {editActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </button>
                      ) : (
                        <Badge className={item.active !== false ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs' : 'bg-red-500/10 text-red-400 border-red-500/20 text-xs'}>
                          {item.active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      {editingId === item.id ? (
                        <Input type="number" value={editPriority} onChange={e => setEditPriority(parseInt(e.target.value) || 0)}
                          className="bg-slate-800 border-slate-700 text-xs h-7 w-16 mx-auto text-center" />
                      ) : (
                        <span className="text-xs text-slate-400 tabular-nums">{item.priority ?? '-'}</span>
                      )}
                    </td>
                    {(tab === 'sports' || tab === 'leagues') && (
                      <td className="px-3 py-3 text-center text-xs text-slate-500">
                        {tab === 'sports' && `${item.leagues_count || 0} leagues, ${item.matches_count || 0} matches`}
                        {tab === 'leagues' && `${item.teams_count || 0} teams, ${item.matches_count || 0} matches`}
                      </td>
                    )}
                    <td className="px-3 py-3 text-center">
                      {editingId === item.id ? (
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" onClick={() => handleSave(item.id)}
                            className="bg-emerald-500/10 text-emerald-400 h-7 w-7 p-0"><Check className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" onClick={() => setEditingId(null)}
                            className="bg-slate-700 text-slate-400 h-7 w-7 p-0"><X className="w-3.5 h-3.5" /></Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => startEdit(item)}
                          className="text-slate-500 hover:text-slate-300 h-7 text-xs"
                          data-testid={`edit-entity-${item.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan="10" className="text-center py-8 text-slate-500 text-sm">No items found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Teams pagination */}
          {tab === 'teams' && totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
              <span>Page {data.page} of {data.pages} ({data.total} total)</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="h-7 border-slate-700 text-slate-400"><ChevronLeft className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}
                  className="h-7 border-slate-700 text-slate-400"><ChevronRight className="w-3 h-3" /></Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
